'use client';

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Pagination, PaginationProps, InlineLoading, Select, SelectItem } from '@carbon/react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import type { PaginatedResponse } from '@/app/types/pagination';
import '@/app/styles/pagination-carbon.scss';
import Link from 'next/link';
interface CarbonPaginationProps<T> {
  fetchData?: (page: number, pageSize: number) => Promise<PaginatedResponse<T>>;
  data?: T[]; // external data
  totalItems?: number; // external total
  pageSize?: number; //external pagesize
  renderItem: (item: T | T[], index?: number) => React.ReactNode; // allow array for event table
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  className?: string;
  listClassName?: string;
  emptyMessage?: string | React.ReactNode;
  isEventTable?: boolean; // NEW flag
  currentPage?: number;
  loading?: boolean;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

function CarbonPagination<T>({
  fetchData,
  data: externalData,
  totalItems: externalTotal,
  loading: externalLoading,
  pageSize: externalPageSize,
  renderItem,
  pageSizeOptions = [12, 24, 36],
  defaultPageSize = 12,
  className,
  listClassName,
  emptyMessage = 'No items found.',
  isEventTable = false,
  currentPage = 1,
  onPageChange,
  onPageSizeChange,
}: CarbonPaginationProps<T>) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const urlPage = searchParams.get('page');

  // Priority: URL param > currentPage prop > default 1
  const getInitialPage = () => {
    if (urlPage) {
      const parsed = parseInt(urlPage, 10);
      return !isNaN(parsed) && parsed > 0 ? parsed : 1;
    }
    return currentPage && currentPage > 0 ? currentPage : 1;
  };

  const initialPage = getInitialPage();

  // Use a ref to track if we've initialized
  const hasInitialized = useRef(false);
  // Use a ref to prevent double loading when page size changes
  const isChangingPageSize = useRef(false);

  const [data, setData] = useState<T[]>(externalData ?? []);
  const [page, setPage] = useState<number>(initialPage);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);
  const isPageSizeControlled = externalPageSize !== undefined;
  const effectivePageSize = isPageSizeControlled ? externalPageSize! : pageSize;
  const [totalItems, setTotalItems] = useState(externalTotal ?? 0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!hasInitialized.current && initialPage !== page) {
      setPage(initialPage);
      hasInitialized.current = true;
    }
  }, []);

  // Sync page when URL changes (but don't trigger if we're already loading)
  useEffect(() => {
    const urlPageNum = urlPage ? parseInt(urlPage, 10) : null;
    if (urlPageNum && urlPageNum !== page && !loading) {
      setPage(urlPageNum);
    }
  }, [urlPage, page, loading]);

  // Sync page when currentPage prop changes (but URL takes priority)
  useEffect(() => {
    if (!urlPage && currentPage && currentPage > 0 && currentPage !== page) {
      setPage(currentPage);
    }
  }, [currentPage, urlPage]);

  useEffect(() => {
    if (isPageSizeControlled && externalPageSize !== undefined && externalPageSize !== pageSize) {
      setPageSize(externalPageSize);
    }
  }, [externalPageSize, isPageSizeControlled, pageSize]);

  useEffect(() => {
    // If total items shrink and current page is now out of range, reset to 1
    if (totalItems > 0 && totalItems <= effectivePageSize * (page - 1)) {
      setPage(1);
      // Call onPageChange in a separate effect or use a ref to avoid infinite loop
      // Don't call it here as it can trigger parent re-renders that change totalItems
    }
  }, [totalItems, page, effectivePageSize]);

  const loadData = useCallback(async () => {
    // Skip loading if we're in the middle of a page size change
    if (isChangingPageSize.current) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (!fetchData) return;
      const response = await fetchData(page, effectivePageSize);
      setData(response?.data);
      if (response?.totalItems !== undefined) {
        setTotalItems(response?.totalItems);
      }
    } catch (err) {
      console.error('Pagination data load failed:', err);
      setError('Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, [fetchData, page, effectivePageSize]);

  // Initial load only - don't reload on every state change
  const hasLoadedInitialData = useRef(false);
  useEffect(() => {
    if (!externalData && fetchData && !hasLoadedInitialData.current) {
      hasLoadedInitialData.current = true;
      loadData();
    }
  }, [externalData, fetchData, loadData]);

  useEffect(() => {
    if (externalData) {
      setData(externalData);
      setTotalItems(externalTotal ?? externalData.length);
      setLoading(externalLoading ?? false);
    }
  }, [externalData, externalTotal, externalLoading]);

  const handleChange: PaginationProps['onChange'] = async ({
    page: nextPage,
    pageSize: nextPageSize,
  }) => {
    const pageChanged = nextPage !== page;
    // Compare against the effective current page size (external or internal)
    const currentEffectivePageSize = isPageSizeControlled ? externalPageSize! : pageSize;
    const pageSizeChanged = nextPageSize !== currentEffectivePageSize;

    // Update state first
    setPage(nextPage);
    if (!isPageSizeControlled) {
      setPageSize(nextPageSize);
    }

    // Then update URL
    if (pageChanged || pageSizeChanged) {
      const params = new URLSearchParams(searchParams.toString());
      if (nextPage === 1) {
        params.delete('page');
      } else {
        params.set('page', nextPage.toString());
      }

      const queryString = params.toString();
      // Ensure trailing slash is present to match canonical URL format (/articles/)
      let normalizedPathname = pathname;
      if (!normalizedPathname.endsWith('/')) {
        normalizedPathname += '/';
      }
      const newUrl = queryString ? `${normalizedPathname}?${queryString}` : normalizedPathname;

      router.push(newUrl, { scroll: false });
    }

    // If parent provides callbacks, let parent handle data fetching
    if (pageChanged && onPageChange) {
      onPageChange(nextPage);
    }

    if (pageSizeChanged && onPageSizeChange) {
      onPageSizeChange(nextPageSize);
    }

    // If no external data and we have fetchData, load data directly
    // This ensures data loads only once per page change or page size change
    if (!externalData && fetchData && (pageChanged || pageSizeChanged)) {
      // For page size changes, we need to fetch with the new size
      const fetchPageSize = pageSizeChanged ? nextPageSize : effectivePageSize;
      const fetchPage = pageSizeChanged ? 1 : nextPage;

      // Wait a tick for state to update
      setTimeout(async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await fetchData(fetchPage, fetchPageSize);
          setData(response?.data);
          if (response?.totalItems !== undefined) {
            setTotalItems(response?.totalItems);
          }
        } catch (err) {
          console.error('Pagination data load failed:', err);
          setError('Failed to load data.');
        } finally {
          setLoading(false);
        }
      }, 0);
    }
  };

  const totalPages = Math.ceil(totalItems / effectivePageSize);

  // Generate page URL
  const getPageUrl = (pageNum: number) => {
    const params = new URLSearchParams(searchParams.toString());
    if (pageNum === 1) {
      params.delete('page');
    } else {
      params.set('page', pageNum.toString());
    }

    let normalizedPathname = pathname;
    if (!normalizedPathname.endsWith('/')) {
      normalizedPathname += '/';
    }
    const queryString = params.toString();
    return queryString ? `${normalizedPathname}?${queryString}` : normalizedPathname.replace(/\/$/, '');
  };

  // Generate visible page numbers
  const getVisiblePages = () => {
    const itemsShown = 7;
    const pages: (number | string)[] = [];

    if (totalPages <= itemsShown) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const leftSiblingIndex = Math.max(page - 1, 1);
      const rightSiblingIndex = Math.min(page + 1, totalPages);

      const shouldShowLeftDots = leftSiblingIndex > 2;
      const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

      if (!shouldShowLeftDots && shouldShowRightDots) {
        const leftItemCount = 5;
        for (let i = 1; i <= leftItemCount; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (shouldShowLeftDots && !shouldShowRightDots) {
        pages.push(1);
        pages.push('...');
        const rightItemCount = 5;
        for (let i = totalPages - rightItemCount + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else if (shouldShowLeftDots && shouldShowRightDots) {
        pages.push(1);
        pages.push('...');
        for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, pageNum: number) => {
    e.preventDefault();
    // handleChange will set loading state, no need to set it here
    handleChange({ page: pageNum, pageSize: effectivePageSize });
  };

  return (
    <div className={className}>
      {loading && <InlineLoading description="Loading..." />}
      {error && <div style={{ color: 'red' }}>{error}</div>}

      {!loading && !error && (
        <>
          {data?.length === 0 ? (
            <p>{emptyMessage}</p>
          ) : (
            <div className={listClassName || undefined} style={{ marginBottom: '1rem' }}>
              {isEventTable
                ? renderItem(data) // Pass the whole array for event table
                : data?.map((item, index) => renderItem(item, index))}
            </div>
          )}

          {totalItems > effectivePageSize && (
            <>
              {/* <Pagination
                data-testid="pagination"
                page={page}
                pageSize={pageSize}
                totalItems={totalItems}
                pageSizes={pageSizeOptions}
                onChange={handleChange}
              />
               */}
              {/* Custom Pagination Nav with Anchor Tags */}
              <nav
                className="cds--pagination-nav"
                aria-label="Pagination navigation"
                data-testid="pagination-nav"
                data-page={page}
                data-page-size={effectivePageSize}
              >
                <div className="cds--pagination-nav__items-info">
                  <div className="cds--pagination-nav__page-size-selector">
                    <span className="cds--pagination-nav__page-size-label">Items per page:</span>

                    <Select
                      id="pagination-nav-page-size"
                      inline
                      hideLabel
                      labelText="Items per page"
                      value={(isPageSizeControlled ? externalPageSize : pageSize).toString()}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        e.preventDefault();
                        const newPageSize = parseInt(e.target.value, 10);
                        const currentSize = isPageSizeControlled ? externalPageSize : pageSize;
                        console.log('Dropdown changed:', {
                          newPageSize,
                          currentPageSize: currentSize,
                          isPageSizeControlled,
                          externalPageSize,
                        });
                        if (!isNaN(newPageSize) && newPageSize !== currentSize) {
                          console.log('🔄 Page size change initiated:', {
                            page: 1,
                            pageSize: newPageSize,
                          });

                          // Set flag to prevent double loading
                          isChangingPageSize.current = true;

                          // Update state immediately to prevent double render
                          setPage(1);
                          if (!isPageSizeControlled) {
                            setPageSize(newPageSize);
                          }

                          // Update URL
                          const params = new URLSearchParams(searchParams.toString());
                          params.delete('page'); // Reset to page 1

                          const queryString = params.toString();
                          let normalizedPathname = pathname;
                          if (!normalizedPathname.endsWith('/')) {
                            normalizedPathname += '/';
                          }
                          const newUrl = queryString ? `${normalizedPathname}?${queryString}` : normalizedPathname;

                          router.push(newUrl, { scroll: false });

                          // Notify parent - this will trigger the actual data fetch
                          if (onPageSizeChange) {
                            onPageSizeChange(newPageSize);
                          }
                          if (onPageChange) {
                            onPageChange(1);
                          }

                          // Reset flag after a short delay to allow parent to handle the change
                          setTimeout(() => {
                            isChangingPageSize.current = false;
                          }, 100);
                        }
                      }}
                      className="cds--pagination-nav__page-size-select"
                    >
                      {pageSizeOptions.map(size => (
                        <SelectItem key={size} value={size.toString()} text={size.toString()} />
                      ))}
                    </Select>
                  </div>
                  <span>
                    Showing {Math.min((page - 1) * effectivePageSize + 1, totalItems)}-
                    {Math.min(page * effectivePageSize, totalItems)} of {totalItems} items
                  </span>
                </div>
                <ul className="cds--pagination-nav__list">
                  <li className="cds--pagination-nav__list-item">
                    <Link
                      href={getPageUrl(Math.max(1, page - 1))}
                      className={`cds--pagination-nav__page ${page === 1 ? 'cds--pagination-nav__page--disabled' : ''}`}
                      aria-label="Previous page"
                      aria-disabled={page === 1}
                      onClick={e => page > 1 && handleLinkClick(e, page - 1)}
                      tabIndex={page === 1 ? -1 : 0}
                    >
                      <svg
                        focusable="false"
                        preserveAspectRatio="xMidYMid meet"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        aria-hidden="true"
                      >
                        <path d="M5 8l5 5 .7-.7L6.4 8l4.3-4.3L10 3z"></path>
                      </svg>
                    </Link>
                  </li>

                  {getVisiblePages().map((pageNum, index) => (
                    <li key={index} className="cds--pagination-nav__list-item">
                      {pageNum === '...' ? (
                        <span className="cds--pagination-nav__page cds--pagination-nav__page--ellipsis">
                          ...
                        </span>
                      ) : (
                        <Link
                          href={getPageUrl(pageNum as number)}
                          className={`cds--pagination-nav__page ${
                            page === pageNum ? 'cds--pagination-nav__page--active' : ''
                          }`}
                          aria-label={`Page ${pageNum}`}
                          aria-current={page === pageNum ? 'page' : undefined}
                          onClick={e => handleLinkClick(e, pageNum as number)}
                        >
                          {pageNum}
                        </Link>
                      )}
                    </li>
                  ))}

                  <li className="cds--pagination-nav__list-item">
                    <Link
                      href={getPageUrl(Math.min(totalPages, page + 1))}
                      className={`cds--pagination-nav__page ${page === totalPages ? 'cds--pagination-nav__page--disabled' : ''}`}
                      aria-label="Next page"
                      aria-disabled={page === totalPages}
                      onClick={e => page < totalPages && handleLinkClick(e, page + 1)}
                      tabIndex={page === totalPages ? -1 : 0}
                    >
                      <svg
                        focusable="false"
                        preserveAspectRatio="xMidYMid meet"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        aria-hidden="true"
                      >
                        <path d="M11 8L6 3l-.7.7L9.6 8l-4.3 4.3L6 13z"></path>
                      </svg>
                    </Link>
                  </li>
                </ul>
              </nav>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default CarbonPagination;
